import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Layout, Flex, Image, Row, Col, Menu, Card } from "antd";
import { getParamsApi } from "../services/api";
import axios from "axios";
import Checklist from "../components/checklist";
import Carousel from "../components/carousel";
import { DoubleRightOutlined, GiftOutlined } from "@ant-design/icons";
import Swal from "sweetalert2";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const commonStyles = {
  width: "100%",
  margin: "0",
  display: "block",
};

const contentWrapperStyles = {
  ...commonStyles,
  padding: "15px",
};

const Main = () => {
  const query = useQuery();
  const [secretKey, setSecretKey] = useState();
  const [email, setEmail] = useState();
  const { Content, Header } = Layout;
  const [loading, setLoading] = useState(false);
  const [contents, setContents] = useState([]);
  const [fanBadges, setFanBadges] = useState([]);
  const [userChecklist, setUserChecklist] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [checklistId, setChecklistId] = useState("");
  const [checklistItems, setChecklistItems] = useState([]);
  const [token, setToken] = useState();
  const [categoryDetails, setCategoryDetails] = useState({});
  const [categoryBadges, setCategoryBadges] = useState({});
  const [infoMessageList, setInfoMessageList] = useState({});
  const [rewards, setRewards] = useState([]);
  const [requestTimer, setRequestTimer] = useState(null);

  // Track page view when component mounts
  useEffect(() => {
    // Send GA4 event for page view
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "page_view",
      page_title: "Fan Desktop Landing Page",
      page_location: window.location.href,
      user_id: secretKey || "anonymous",
      user_email: email || "anonymous",
      view_timestamp: new Date().toISOString(),
    });
    // 'ga4_measurement_id': 'G-EC4X5M443P'

    // if (window.gtag) {
    //   window.gtag('event', 'page_view', {
    //     'page_title': 'Fan Desktop Landing Page',
    //     'page_location': window.location.href,
    //     'user_id': secretKey || 'anonymous',
    //     'user_email': email || 'anonymous',
    //     'view_timestamp': new Date().toISOString()
    //   });
    // }
  }, [secretKey, email]);

  useEffect(() => {
    const secretKeyFromUrl = query.get("secretKey");
    const emailFromUrl = query.get("email");

    if (secretKeyFromUrl && emailFromUrl) {
      localStorage.setItem("secretKey", secretKeyFromUrl);
      localStorage.setItem("email", emailFromUrl);
      setSecretKey(secretKeyFromUrl);
      setEmail(emailFromUrl);
    }
  }, [query]);

  useEffect(() => {
    if (secretKey && email) {
      if (requestTimer) {
        clearTimeout(requestTimer);
      }
      const timer = setTimeout(() => {
        getFanBadges(secretKey, email);
      }, 200);
      setRequestTimer(timer);

      return () => clearTimeout(timer);
    }
  }, [secretKey, email]);

  const getToken = async () => {
    const params = new URLSearchParams();
    params.append("grant_type", "client_credentials");
    params.append("client_id", process.env.REACT_APP_CLIENT_ID);
    params.append("client_secret", process.env.REACT_APP_CLIENT_SECRET);
    const config = {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    };
    try {
      const response = await axios.post(
        process.env.REACT_APP_TOKEN_URL,
        params,
        config,
      );
      const newToken =
        response.data.token_type + " " + response.data.access_token;
      localStorage.setItem("token", newToken);
      setToken(newToken);
      const now = new Date();
      const expirationTime = new Date(
        now.getTime() + response.data.expires_in * 1000,
      );
      localStorage.setItem("tokenExpirationTime", expirationTime);
    } catch (error) {
      console.error("Error fetching the token:", error);
    }
  };

  useEffect(() => {
    const expiryTime = localStorage.getItem("tokenExpirationTime");
    const parsedExpiryTime = expiryTime ? new Date(expiryTime) : null;
    const storedToken = localStorage.getItem("token");

    if (
      storedToken &&
      parsedExpiryTime &&
      parsedExpiryTime.getTime() > new Date().getTime()
    ) {
      setToken(storedToken);
      getTemplate();
      getInfoMessage();
    } else {
      getToken();
    }
  }, []);

  useEffect(() => {
    if (categoryId) {
      getCategoryBadges(categoryId);
    }
  }, [categoryId]);

  useEffect(() => {
    if (checklistId) {
      getUserChecklist(checklistId);
    }
  }, [checklistId]);

  const waitForToken = () => {
    return new Promise((resolve) => {
      const checkToken = () => {
        if (token) {
          resolve();
        } else {
          setTimeout(checkToken, 100);
        }
      };
      checkToken();
    });
  };

  const getParamsApiWithTokenWait = async (url, params) => {
    await waitForToken();
    return getParamsApi(url, params);
  };

  const getTemplate = () => {
    setLoading(true);
    getParamsApiWithTokenWait("/template/published", { deviceType: "DESKTOP" })
      .then((template) => {
        if (template && template.isActive) {
          setContents(template.items);
          template.items.forEach((item) => {
            if (item.type === "CATEGORY") {
              setCategoryId(item.templateItemId);
              getCategoryBadges(item.templateItemId);
              getUserCategory(item.templateItemId);
            } else if (item.type === "CHECKLIST") {
              setChecklistId(item.templateItemId);
            }
          });
        } else {
          console.warn("No active template found");
          setContents([]);
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const getUserCategory = (id) => {
    setLoading(true);
    const params = { id: id };
    getParamsApiWithTokenWait(`/category/${id}`, params)
      .then((response) => {
        setCategoryDetails((prevDetails) => ({
          ...prevDetails,
          [id]: {
            name: response.name,
            placementType: response.placementType,
            infoMessage: response.infoMessage,
            iconURL: response.iconURL,
            backgroundImageURL: response.backgroundImageURL,
            fontColor: response.fontColor,
            font: response.font,
            lineColor: response.lineColor,
            shape: response.shape,
            layout: response.layout,
            paddingTop: response.paddingTop,
            paddingBottom: response.paddingBottom,
            paddingRight: response.paddingRight,
            paddingLeft: response.paddingLeft,
            infoBackgroundColor: response.infoBackgroundColor,
            infoFontColor: response.infoFontColor,
            tagBackgroundColor: response.tagBackgroundColor,
            tagFontColor: response.tagFontColor,
            tagMessage: response.tagMessage,
            buttonColor: response.buttonColor,
            buttonLinkURL: response.buttonLinkURL,
            buttonName: response.buttonName,
            buttonTextColor: response.buttonTextColor,
            emptyCategoryDescription: response.emptyCategoryDescription,
          },
        }));
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const getCategoryBadges = (categoryId) => {
    setLoading(true);
    const params = { categoryId: categoryId };

    getParamsApiWithTokenWait(`/badge/category/${categoryId}`, params)
      .then((response) => {
        response.forEach((badge) => {
          console.log(
            `Desktop Badge: ${badge.name}, categoryOrder: ${badge.categoryOrder}, type: ${typeof badge.categoryOrder}`,
          );
        });

        const activeBadges = response
          .filter((badge) => badge.isActive)
          .sort((a, b) => {
            const orderA = parseInt(a.categoryOrder) || 999999;
            const orderB = parseInt(b.categoryOrder) || 999999;
            console.log(
              `Desktop Sıralama: ${a.name} (${orderA}) vs ${b.name} (${orderB})`,
            );
            return orderA - orderB;
          });

        console.log(
          "Desktop Sıralanmış activeBadges:",
          activeBadges.map((b) => ({ name: b.name, order: b.categoryOrder })),
        );

        setCategoryBadges((prevBadges) => ({
          ...prevBadges,
          [categoryId]: activeBadges,
        }));
        setLoading(false);
      })
      .catch((error) => {
        console.error("Desktop getCategoryBadges Error:", error);
        setLoading(false);
      });
  };

  const getFanBadges = (secretKey, email) => {
    setLoading(true);
    const params = { secretKey, email };

    getParamsApiWithTokenWait(`/badge/fan`, params)
      .then((response) => {
        const activeBadges = response.badgeFanDTOList.filter(
          (badge) => badge.isActive,
        );
        setFanBadges(activeBadges);

        const rewardsMap = new Map();

        activeBadges.forEach((badge) => {
          if (
            badge.rewardDescription ||
            badge.rewardDescriptionFontColor ||
            badge.rewardDescriptionFontSize ||
            badge.rewardHeadline ||
            badge.rewardHeadlineFontColor ||
            badge.rewardHeadlineFontSize ||
            badge.rewardImageURL
          ) {
            rewardsMap.set(badge.id, {
              badgeId: badge.id,
              rewardDetails: {
                rewardDescription: badge.rewardDescription || "",
                descriptionFontColor:
                  badge.rewardDescriptionFontColor || "#ffffff",
                descriptionFontSize: badge.rewardDescriptionFontSize || "12",
                headline: badge.rewardHeadline || "",
                headlineFontColor: badge.rewardHeadlineFontColor || "#ffffff",
                headlineFontSize: badge.rewardHeadlineFontSize || "12",
                imageURL: badge.rewardImageURL || "",
              },
            });
          }
        });

        const fanRewards = Array.from(rewardsMap.values());

        setRewards((prevRewards) => {
          const newRewardsMap = new Map(
            prevRewards.map((reward) => [reward.badgeId, reward]),
          );

          fanRewards.forEach((reward) => {
            newRewardsMap.set(reward.badgeId, reward);
          });

          const updatedRewards = Array.from(newRewardsMap.values());
          return updatedRewards;
        });

        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching fan badges:", error);

        Swal.fire({
          icon: "error",
          title: "Authentication Error",
          text: "We could not verify your credentials. Please check your login information or try again later.",
          confirmButtonColor: "#0050b5",
          confirmButtonText: "OK",
        });

        setLoading(false);
      });
  };

  const getUserChecklist = (checklistId) => {
    setLoading(true);
    const params = { id: checklistId };
    getParamsApiWithTokenWait(`/checklist/${checklistId}`, params)
      .then((response) => {
        setUserChecklist(response);
        setChecklistItems(response.items);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching checklist:", error);
        setLoading(false);
      });
  };

  const getInfoMessage = () => {
    setLoading(true);
    getParamsApiWithTokenWait("/infoMessage/list", {})
      .then((infoMessage) => {
        setInfoMessageList(infoMessage);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error while fetching info message:", error);
        setLoading(false);
      });
  };

  const navigate = useNavigate();

  const renderContent = (content) => {
    const categoryInfo = categoryDetails[content.templateItemId];
    switch (content.type) {
      case "TEXT":
        const textStyles = content.styles ? JSON.parse(content.styles) : {};
        return (
          <div style={{ ...commonStyles, padding: "10px 0px" }}>
            <p
              dangerouslySetInnerHTML={{
                __html: content.title || "Default Text",
              }}
              style={{
                margin: 0,
                textAlign: "left",
                color: "#fff",
                ...commonStyles,
                ...textStyles,
              }}
            />
          </div>
        );
      case "INFO":
        return (
          infoMessageList[content.templateItemId] && (
            <div
              key={infoMessageList[content.templateItemId].id}
              style={{
                ...contentWrapperStyles,
                background:
                  infoMessageList[content.templateItemId].backgroundColor,
                backgroundImage: `url(${infoMessageList[content.templateItemId].backgroundImageURL})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                borderRadius: "5px",
                ...commonStyles,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                {infoMessageList[content.templateItemId].iconURL && (
                  <img
                    src={infoMessageList[content.templateItemId].iconURL}
                    alt="Icon"
                    style={{ width: "35px", height: "35px" }}
                  />
                )}
                <span style={{ color: "#fff", flex: 1 }}>
                  {infoMessageList[content.templateItemId].description}
                </span>
              </div>
            </div>
          )
        );
      case "LINK":
        return (
          <div style={{ ...commonStyles, ...commonStyles }}>
            <a
              className="custom-link"
              href={content.description}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "block" }}
            >
              {content.title || "Default Text"}
            </a>
          </div>
        );
      case "IMAGE":
        return (
          <div
            style={{
              ...commonStyles,
              overflow: "hidden",
              ...commonStyles,
            }}
          >
            <img
              src={content.url}
              alt={content.description || "Image"}
              style={{
                objectFit: "contain",
                display: "block",
                ...commonStyles,
              }}
            />
          </div>
        );
      case "VIDEO":
        return (
          <div
            key={content.templateItemId}
            className="video-responsive"
            style={{
              ...commonStyles,
              position: "relative",
              paddingBottom: "56.25%",
              height: 0,
              ...commonStyles,
            }}
          >
            <iframe
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
              }}
              src={content.url}
              allow="autoplay; encrypted-media"
              allowFullScreen
              title={content.title || "Video"}
            />
          </div>
        );
      case "CHECKLIST":
        return (
          <div style={{ ...commonStyles, ...commonStyles }}>
            <Checklist
              data={userChecklist}
              items={checklistItems}
              fanBadges={fanBadges}
            />
          </div>
        );
      case "CATEGORY":
        return (
          <div
            key={content.templateItemId}
            style={{
              ...commonStyles,
              borderRadius: categoryInfo?.shape === "Rounded" ? "10px" : "0",
              padding: `${categoryInfo?.paddingTop || "15spx"} ${
                categoryInfo?.paddingRight || "15px"
              } ${categoryInfo?.paddingBottom || "15px"} ${
                categoryInfo?.paddingLeft || "15px"
              }`,
              backgroundImage: `url(${categoryInfo?.backgroundImageURL || ""})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              overflow: "hidden",
              ...commonStyles,
            }}
          >
            <Flex
              align="center"
              style={{
                margin: "0 15px",
                borderBottom: `1px solid ${categoryInfo?.lineColor || "white"}`,
              }}
            >
              {categoryInfo?.iconURL && (
                <Image
                  src={categoryInfo.iconURL}
                  alt="Category Icon"
                  className="category-icon"
                  style={{ width: "25px" }}
                />
              )}
              <h1
                className="landing-headline"
                style={{
                  color: categoryInfo?.fontColor || "#fff",
                }}
              >
                {categoryInfo?.name || ""}
              </h1>
            </Flex>

            {(categoryInfo?.infoMessage || categoryInfo?.tagMessage) && (
              <Flex
                className="info-card"
                style={{
                  background: categoryInfo.infoBackgroundColor,
                  color: categoryInfo.infoFontColor,
                }}
              >
                {categoryInfo?.tagMessage && (
                  <div
                    className="info-tag"
                    style={{
                      background: categoryInfo?.tagBackgroundColor,
                      color: categoryInfo?.tagFontColor,
                    }}
                  >
                    {categoryInfo.tagMessage}
                  </div>
                )}
                {categoryInfo?.infoMessage && (
                <div
                  className="info-message"
                  style={{
                    color: categoryInfo.infoFontColor,
                    background: categoryInfo.infoBackgroundColor,
                  }}
                >
                  {categoryInfo.infoMessage}
                </div>
                )}
              </Flex>
            )}

            {(() => {
              const badgesForCategory =
                categoryBadges[content.templateItemId] || [];
              const fanBadgesForCategory = fanBadges.filter(
                (badge) => badge.categoryId === content.templateItemId,
              );
              const allBadges = badgesForCategory.concat(fanBadgesForCategory);

              const combinedBadges = allBadges
                .reduce((acc, badge) => {
                  const existingBadge = acc.find(
                    (item) => item.id === badge.id,
                  );
                  const fanVersion = fanBadgesForCategory.find(
                    (fanBadge) => fanBadge.id === badge.id,
                  );

                  if (existingBadge) {
                    existingBadge.isEarned = fanVersion
                      ? fanVersion.badgeCompletion
                      : existingBadge.isEarned || false;
                  } else {
                    acc.push({
                      ...badge,
                      id: badge.id,
                      name: badge.name,
                      description: badge.description,
                      imageURL: badge.imageURL,
                      isEarned: fanVersion ? fanVersion.badgeCompletion : false,
                      isDisplayUnearned: badge.isDisplayUnearned,
                      categoryId: badge.categoryId,
                      rewardDescription: badge.rewardDescription,
                      rewardDescriptionFontColor:
                        badge.rewardDescriptionFontColor,
                      rewardDescriptionFontSize:
                        badge.rewardDescriptionFontSize,
                      rewardHeadline: badge.rewardHeadline,
                      rewardHeadlineFontColor: badge.rewardHeadlineFontColor,
                      rewardHeadlineFontSize: badge.rewardHeadlineFontSize,
                      rewardImageURL: badge.rewardImageURL,
                    });
                  }
                  return acc;
                }, [])
                .sort((a, b) => {
                  const orderA = parseInt(a.categoryOrder) || 999999;
                  const orderB = parseInt(b.categoryOrder) || 999999;
                  console.log(
                    `Desktop Final sıralama: ${a.name} (${orderA}) vs ${b.name} (${orderB})`,
                  );
                  return orderA - orderB;
                });

              console.log(
                "Desktop Final combinedBadges sırası:",
                combinedBadges.map((b) => ({
                  name: b.name,
                  order: b.categoryOrder,
                })),
              );

              const displayedBadges = combinedBadges.filter((badge) => {
                if (badge.isEarned) {
                  return true;
                }
                return badge.isDisplayUnearned === true;
              });

              console.log(
                "Desktop Filtered displayedBadges:",
                displayedBadges.map((b) => ({
                  name: b.name,
                  isEarned: b.isEarned,
                  isDisplayUnearned: b.isDisplayUnearned,
                })),
              );

              return (
                <div
                  style={{
                    width: "100%",
                    margin: `0 ${categoryInfo?.paddingLeft || "15px"} 0 ${categoryInfo?.paddingRight || "15px"}`,
                  }}
                >
                  {displayedBadges.length > 0 ? (
                    <Carousel
                      slides={displayedBadges.map((badge) => ({
                        ...badge,
                        name: badge.name || "",
                        description: badge.description || "",
                        imageURL: badge.imageURL || "",
                        isEarned: badge.isEarned || false,
                        isDisplayUnearned: badge.isDisplayUnearned || false,
                        categoryId: badge.categoryId || "",
                        categoryOrder: badge.categoryOrder || 0,
                        rewardDescription: badge.rewardDescription || "",
                        rewardDescriptionFontColor:
                          badge.rewardDescriptionFontColor || "#ffffff",
                        rewardDescriptionFontSize:
                          badge.rewardDescriptionFontSize || "12",
                        rewardHeadline: badge.rewardHeadline || "",
                        rewardHeadlineFontColor:
                          badge.rewardHeadlineFontColor || "#ffffff",
                        rewardHeadlineFontSize:
                          badge.rewardHeadlineFontSize || "12",
                        rewardImageURL: badge.rewardImageURL || "",
                      }))}
                    />
                  ) : (
                    <Card
                      style={{
                        backgroundColor: "black",
                        textAlign: "center",
                        border: "none",
                      }}
                    >
                      <div
                        style={{ color: "white", opacity: ".5" }}
                        dangerouslySetInnerHTML={{
                          __html: categoryInfo?.emptyCategoryDescription || "",
                        }}
                      />
                    </Card>
                  )}
                </div>
              );
            })()}

            {categoryInfo?.buttonLinkURL && (
              <a
                style={{
                  position: "relative",
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "inline-block",
                  color: categoryInfo.buttonTextColor,
                  backgroundColor: categoryInfo.buttonColor,
                  textDecoration: "none",
                  borderRadius: "5px",
                  padding: "5px 5px 5px 10px",
                  width: "120px",
                  margin: "10px 0",
                }}
                href={`https://${categoryInfo.buttonLinkURL}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Flex align="center">
                  <span
                    style={{
                      marginRight: "5px",
                      fontSize: "13px",
                    }}
                  >
                    {categoryInfo.buttonName}
                  </span>
                  <DoubleRightOutlined style={{ opacity: ".5" }} />
                </Flex>
              </a>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    document.title = "Orlando Magic Fan";
  }, []);

  return (
    <Layout className="main-container" style={{ background: "#000" }}>
      <Header
        style={{
          background: "#A9A9A9",
          padding: 0,
          height: "40px",
          position: "sticky",
          top: 0,
          zIndex: 1,
          width: "100%",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <Menu
          mode="horizontal"
          style={{
            lineHeight: "40px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.03)",
            background: "#000",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Menu.Item
            key="gift"
            icon={<GiftOutlined style={{ color: "#fff" }} />}
            onClick={() => navigate("/rewards-desktop", { state: { rewards } })}
            style={{
              marginRight: "20px",
              color: "#fff",
            }}
          >
            Rewards
          </Menu.Item>
        </Menu>
      </Header>

      <Content style={{ background: "#000", minHeight: "100vh" }}>
        <Row gutter={[24, 24]}>
          {contents &&
            contents.length > 0 &&
            Object.entries(
              contents.reduce((acc, item) => {
                const rowId = item.rowId || "single";
                if (!acc[rowId]) {
                  acc[rowId] = [];
                }
                acc[rowId].push(item);
                return acc;
              }, {}),
            ).map(([rowId, items]) => {
              items.sort((a, b) => a.order - b.order);

              return (
                <Row
                  key={rowId}
                  style={{ width: "100%", marginBottom: "24px" }}
                >
                  {items.map((content) => (
                    <Col
                      key={content.id}
                      span={24 / items.length}
                      style={{
                        padding: "0 12px",
                      }}
                    >
                      {renderContent(content)}
                    </Col>
                  ))}
                </Row>
              );
            })}
        </Row>
      </Content>
    </Layout>
  );
};

export default Main;
